import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getUserStatus } from './services/apiService';

function StatusTracker({ isOpen: externalIsOpen, onClose }) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onClose || setInternalIsOpen;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusData, setStatusData] = useState(null);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setError(null);
      setStatusData(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError(null);
    setStatusData(null);

    try {
      const data = await getUserStatus(email.trim());
      setStatusData(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Something went wrong while fetching status');
      setStatusData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setEmail('');
    setError(null);
    setStatusData(null);
    if (onClose) onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      {externalIsOpen === undefined && (
        <button
          type="button"
          className="link-btn"
          onClick={() => setIsOpen(true)}
        >
          Track Status
        </button>
      )}

      {isOpen &&
        createPortal(
          <div className="status-modal-overlay" onClick={handleClose}>
            <div className="status-modal" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="status-modal-close"
                onClick={handleClose}
                aria-label="Close"
              >
                ×
              </button>
              <h2>Track Your Status</h2>
              <p className="status-modal-subtitle">
                Enter your email to check your training program status
              </p>

              <form onSubmit={handleSubmit} className="status-form">
                <label>
                  Email
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    disabled={loading}
                  />
                </label>
                <button type="submit" className="primary" disabled={loading}>
                  {loading ? 'Checking...' : 'Check Status'}
                </button>
              </form>

              {error && (
                <div className="status-error">
                  <p>{error}</p>
                </div>
              )}

              {statusData && (
                <div className="status-results">
                  <h3>Your Status</h3>
                  <div className="status-info-grid">
                    <div className="status-info-item">
                      <span className="status-label">Name:</span>
                      <span className="status-value">{statusData.name}</span>
                    </div>
                    <div className="status-info-item">
                      <span className="status-label">Email:</span>
                      <span className="status-value">{statusData.email}</span>
                    </div>
                    <div className="status-info-item">
                      <span className="status-label">Start Date:</span>
                      <span className="status-value">{formatDate(statusData.startDate)}</span>
                    </div>
                    <div className="status-info-item">
                      <span className="status-label">End Date:</span>
                      <span className="status-value">{formatDate(statusData.endDate)}</span>
                    </div>
                    <div className="status-info-item status-info-item-special">
                      <span className="status-label">Status:</span>
                      <span className={`status-badge status-${statusData.status?.toLowerCase() || 'unknown'}`}>
                        {statusData.status || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default StatusTracker;

